import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, RefreshCw, Flag, Info, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { FameSlider, fameColor, mixColor } from "../components/FameSliders";
import { TuneCard } from "../components/TuneCard";
import { AlbumNav } from "../components/AlbumNav";
import { AppHeader } from "../components/AppHeader";
import { useAuth } from "../contexts/AuthContext";
import { getFlags } from "../lib/flags";
import { saveScroll, readView, restoreScroll } from "../lib/scrollMemory";
import {
  getMeta,
  searchByName,
  searchByNotes,
  searchByLink,
  searchByFame,
  forceRefresh,
  refreshStatus,
} from "../lib/api";

const ANY = "__any__";
const SCROLL_KEY = "foinn_search_view";

const guestSearchState = () => {
  const today = new Date().toISOString().slice(0, 10);
  let s;
  try {
    s = JSON.parse(localStorage.getItem("foinn_guest_searches") || "{}");
  } catch {
    s = {};
  }
  if (s.date !== today) s = { date: today, count: 0 };
  return s;
};

const bumpGuestSearch = () => {
  const s = guestSearchState();
  s.count = (s.count || 0) + 1;
  localStorage.setItem("foinn_guest_searches", JSON.stringify(s));
};

export default function SearchPage() {
  const { user, openPricing } = useAuth();
  const navigate = useNavigate();
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("foinn_search") || "null");
    } catch {
      return null;
    }
  })();

  const [meta, setMeta] = useState({ types: [], keys: [], tune_count: 0, last_import: null, limits: {} });
  const [name, setName] = useState(saved?.name || "");
  const [textMode, setTextMode] = useState(saved?.textMode || "name");
  const [total, setTotal] = useState(saved?.total || 0);
  const [linkInfo, setLinkInfo] = useState(saved?.linkInfo || null);
  const [type, setType] = useState(saved?.type || ANY);
  const [tkey, setTkey] = useState(saved?.tkey || ANY);
  const [rareFamous, setRareFamous] = useState(saved?.rareFamous ?? 100);
  const [hideFlagged, setHideFlagged] = useState(saved?.hideFlagged ?? false);

  const [results, setResults] = useState(saved?.results || []);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(saved?.searched || false);
  const [modeUsed, setModeUsed] = useState(saved?.modeUsed || "");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    getMeta().then(setMeta).catch(() => {});
  }, []);

  // remember the scroll position so Back from a tune lands on the same card
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => saveScroll(SCROLL_KEY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    if (saved?.searched) return restoreScroll(readView(SCROLL_KEY)?.y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep search state across navigation (Back from a tune restores the same results)
  useEffect(() => {
    sessionStorage.setItem(
      "foinn_search",
      JSON.stringify({ name, textMode, total, linkInfo, type, tkey, rareFamous, hideFlagged, results, modeUsed, searched })
    );
  }, [name, textMode, total, linkInfo, type, tkey, rareFamous, hideFlagged, results, modeUsed, searched]);

  const c1 = fameColor(rareFamous);
  const c2 = fameColor(Math.min(100, rareFamous + 22));

  const runSearch = useCallback(async () => {
    const guestLimit = meta.limits?.guest_daily_searches ?? 5;
    if (!user) {
      const s = guestSearchState();
      if ((s.count || 0) >= guestLimit) {
        toast.error(`Guests get ${guestLimit} searches a day. Sign in for 20 a day — or go Premium for unlimited.`);
        openPricing();
        return;
      }
    }
    setLoading(true);
    setSearched(true);
    const typeVal = type === ANY ? "" : type;
    const keyVal = tkey === ANY ? "" : tkey;
    try {
      let data;
      const isLink = /^https?:\/\//i.test(name.trim());
      if (isLink) {
        data = await searchByLink(name.trim());
        setLinkInfo(data.link);
        setModeUsed("link");
      } else if (name.trim() && textMode === "notes") {
        data = await searchByNotes(name.trim(), typeVal, keyVal);
        setModeUsed("notes");
      } else if (name.trim()) {
        data = await searchByName(name.trim(), typeVal, keyVal);
        setModeUsed("name");
      } else {
        data = await searchByFame({
          rare_famous: rareFamous,
          rarely_often: rareFamous,
          type: typeVal || null,
          key: keyVal || null,
          exclude_ids: hideFlagged ? [...new Set(getFlags().map((f) => f.tune_id))] : [],
        });
        setModeUsed("fame");
      }
      setResults(data.results || []);
      setTotal(data.total_capped ? `${data.total}+` : data.total || 0);
      if (!user) bumpGuestSearch();
    } catch (e) {
      if ([400, 422].includes(e?.response?.status) && e.response.data?.detail) {
        toast.error(e.response.data.detail);
      } else if (e?.response?.status === 429) {
        toast.error(e.response.data?.detail || "Daily search limit reached");
        openPricing();
      } else {
        toast.error("Search failed. Please try again.");
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [name, textMode, type, tkey, rareFamous, hideFlagged, user, meta.limits, openPricing]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await forceRefresh();
      toast.success("Refresh started — pulling the latest tunes from thesession.org");
      const poll = setInterval(async () => {
        const s = await refreshStatus();
        if (!s.running) {
          clearInterval(poll);
          setRefreshing(false);
          getMeta().then(setMeta);
          toast.success("Database updated");
        }
      }, 5000);
    } catch (e) {
      setRefreshing(false);
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        toast.error("Manual refresh is for Premium members — data refreshes monthly for everyone");
        openPricing();
      } else {
        toast.error("Could not start refresh");
      }
    }
  };

  const searchesLeft = user
    ? user.unlimited_searches
      ? null
      : user.search_quota?.left
    : Math.max(0, (meta.limits?.guest_daily_searches ?? 5) - (guestSearchState().count || 0));

  return (
    <div className="min-h-screen">
      <AppHeader />
      <section className="mx-auto max-w-3xl px-5 pt-8">
        <div
          className="relative overflow-hidden rounded-[2rem] border border-[#e5e0d8] bg-[#fffdf9] p-6 shadow-[0_20px_60px_rgba(45,40,36,0.08)] md:p-9"
          style={{
            boxShadow: `0 20px 60px rgba(45,40,36,0.08), 0 0 0 4px ${c1}12`,
          }}
        >
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20 blur-3xl transition-colors duration-200"
            style={{ backgroundColor: c1 }}
          />
          <div
            className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full opacity-20 blur-3xl transition-colors duration-200"
            style={{ backgroundColor: c2 }}
          />

          <h2 className="relative font-serif text-4xl font-bold text-[#2d2824] sm:text-5xl">
            Find your tune
          </h2>

          {/* name or notes */}
          <div className="relative mt-6">
            <div className="mb-2 flex gap-1.5" data-testid="search-text-mode">
              {[
                ["name", "By name"],
                ["notes", "By notes (ABC)"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setTextMode(id)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-extrabold transition-colors ${
                    textMode === id ? "bg-[#2d2824] text-white" : "border border-[#e5e0d8] bg-white text-[#6e655b]"
                  }`}
                  data-testid={`search-mode-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#a89e92]" />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder={
                  textMode === "notes" ? "Notes in ABC (e.g. FAA BAA) — at least 4" : "Tune name or paste a track link…"
                }
                className="w-full rounded-2xl border border-[#e5e0d8] bg-white py-4 pl-12 pr-11 text-lg font-semibold text-[#2d2824] outline-none transition-shadow placeholder:font-normal placeholder:text-[#a89e92] focus:ring-2 focus:ring-[#2d2824]/20"
                data-testid="search-name-input"
              />
              {name && (
                <button
                  onClick={() => setName("")}
                  className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[#a89e92] transition-colors hover:bg-[#f3efea] hover:text-[#2d2824]"
                  aria-label="Clear search"
                  data-testid="search-clear-button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* type + key */}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
                  Type of tune
                </label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger
                    className="h-12 rounded-2xl border-[#e5e0d8] bg-white text-base font-semibold"
                    data-testid="type-select"
                  >
                    <SelectValue placeholder="Any type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value={ANY}>Any type</SelectItem>
                    {meta.types.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
                  Tonality / key
                </label>
                <Select value={tkey} onValueChange={setTkey}>
                  <SelectTrigger
                    className="h-12 rounded-2xl border-[#e5e0d8] bg-white text-base font-semibold"
                    data-testid="key-select"
                  >
                    <SelectValue placeholder="Any key" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value={ANY}>Any key</SelectItem>
                    {meta.keys.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* fame */}
          <div className="relative mt-8 rounded-3xl border border-[#e5e0d8] bg-[#fbf9f4] p-5">
            <FameSlider value={rareFamous} setValue={setRareFamous} />
          </div>

          {/* big search button */}
          <button
            onClick={runSearch}
            disabled={loading}
            className="group relative mt-7 flex h-20 w-full items-center justify-center gap-3 rounded-full text-2xl font-black text-white shadow-[0_14px_0_rgba(0,0,0,0.12),0_20px_40px_rgba(45,40,36,0.25)] transition-transform duration-150 hover:scale-[1.015] active:translate-y-1 active:shadow-[0_6px_0_rgba(0,0,0,0.12),0_10px_20px_rgba(45,40,36,0.25)] disabled:opacity-70"
            style={{ background: `linear-gradient(120deg, ${c1}, ${c2})` }}
            data-testid="search-button"
          >
            {loading ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : (
              <>
                <Search className="h-8 w-8" strokeWidth={2.5} />
                Search Tunes
              </>
            )}
          </button>
          {!name.trim() && (
            <>
              <p className="relative mt-3 text-center text-xs font-semibold text-[#a89e92]">
                Ranked by fame · each search reshuffles fresh discoveries
              </p>
              <label
                className="relative mt-2 flex cursor-pointer items-center justify-center gap-2 text-xs font-bold text-[#6e655b]"
                data-testid="hide-flagged-toggle"
              >
                <input
                  type="checkbox"
                  checked={hideFlagged}
                  onChange={(e) => setHideFlagged(e.target.checked)}
                  className="h-4 w-4 accent-[#2d2824]"
                  data-testid="hide-flagged-checkbox"
                />
                <Flag className="h-3.5 w-3.5" /> Skip tunes I already flagged
              </label>
            </>
          )}
          {searchesLeft !== null && searchesLeft !== undefined && (
            <button
              onClick={openPricing}
              className="relative mt-2 w-full text-center text-xs font-bold text-[#6e655b] hover:underline"
              data-testid="searches-left"
            >
              {searchesLeft} search{searchesLeft === 1 ? "" : "es"} left
              {user?.search_extra ? ` (incl. ${user.search_extra} extra)` : " today"} ·{" "}
              {user ? "go Premium for unlimited" : "sign in for 20 a day"}
            </button>
          )}
        </div>
      </section>

      {/* results */}
      <section className="mx-auto max-w-6xl px-5 pb-24 pt-10">
        {loading && (
          <div className="flex flex-col items-center gap-3 py-16 text-[#6e655b]">
            <Loader2 className="h-10 w-10 animate-spin" />
            <span className="font-semibold">Searching the session…</span>
          </div>
        )}

        {!loading && modeUsed === "link" && linkInfo?.recording && (
          <AlbumNav
            linkInfo={linkInfo}
            onTrack={(res, info) => {
              setResults(res);
              setLinkInfo(info);
            }}
          />
        )}

        {!loading && searched && results.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <img
              src="https://images.unsplash.com/photo-1460036521480-ff49c08c2781?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxmaWRkbGUlMjBmb2xrJTIwbXVzaWN8ZW58MHx8fHwxNzg4MDg1MjI0fDA&ixlib=rb-4.1.0&q=85"
              alt="fiddle"
              className="h-40 w-40 rounded-full object-cover opacity-80 grayscale"
            />
            <p className="font-serif text-2xl font-bold text-[#2d2824]">No tunes found</p>
            <p className="max-w-sm text-sm text-[#6e655b]">
              Try a different name, or loosen your type/key filters.
            </p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <>
            <div className="mb-4 flex items-center gap-2 text-sm font-bold text-[#6e655b]">
              <span className="rounded-full bg-[#2d2824] px-3 py-1 text-white">
                {results.length} result{results.length > 1 ? "s" : ""}
              </span>
              {modeUsed === "fame" && <span>ranked by fame · reshuffled each search</span>}
              {modeUsed === "name" && <span>most relevant matches first</span>}
              {modeUsed === "link" && linkInfo && (
                <span data-testid="link-info">
                  {linkInfo.source}: {linkInfo.artist.replace("|", " · ")} — {linkInfo.title}
                  {linkInfo.recording
                    ? ` · on thesession.org, track ${linkInfo.recording.track}`
                    : " · not listed as a recording, matched by tune names"}
                </span>
              )}
              {modeUsed === "notes" && (
                <span data-testid="notes-total">
                  {parseInt(total, 10) > results.length ? `top ${results.length} of ${total} tunes` : "tunes"} containing these
                  notes · most famous first
                </span>
              )}
            </div>
            <div className="mx-auto grid max-w-3xl grid-cols-1 gap-5">
              {results.map((t, i) => (
                <TuneCard key={t.tune_id} tune={t} index={i} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* footer */}
      <footer className="border-t border-[#e5e0d8] bg-[#fbf9f4] py-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 text-xs text-[#6e655b] sm:flex-row">
          <span>
            Data from{" "}
            <a href="https://thesession.org" className="font-bold underline" target="_blank" rel="noreferrer">
              thesession.org
            </a>{" "}
            · {meta.tune_count.toLocaleString()} tunes
            {meta.last_import && ` · updated ${new Date(meta.last_import).toLocaleDateString()}`}
          </span>
          <button
            onClick={() => navigate("/about")}
            className="flex items-center gap-1.5 rounded-full bg-[#8a2be2] px-4 py-2 text-xs font-extrabold text-white shadow-[0_6px_18px_rgba(138,43,226,0.35)] transition-transform hover:scale-105 active:scale-95"
            data-testid="more-info-link"
          >
            <Info className="h-4 w-4" /> More info · guides & pricing
          </button>
          {(user?.unlimited_searches || user?.is_admin) && (
            <button
              onClick={doRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-full border border-[#e5e0d8] bg-white px-3 py-1.5 font-bold text-[#2d2824] hover:bg-[#f3efea] disabled:opacity-60"
              data-testid="refresh-button"
              title="Pull the latest data now"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh data"}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
